import {
  Avatar,
  AvatarGroup,
  Stack,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty, take } from 'lodash-es';
import { useRouter } from 'next/navigation';
import StyledBoldTypography from '../Styled/StyledBoldTypography';
import getQualifiedImageUrl from '../../utils/imageUrlProcessor';
import { getAvatarText, getFullName } from '../../utils/stringHelpers';
import PostContributingUserPropType from './postContributingUserPropType';
import ClickableTooltip from '../Styled/ClickableTooltip';
import StyledLinkButton from '../Styled/styledLinkButton';
import StyledAnchor from '../Styled/StyledAnchor';
import PostPropType from './postPropType';
import useStore from '../../stores/store';

const StyledAvatarGroup = styled(AvatarGroup)(() => ({
  '& .MuiAvatar-root': {
    cursor: 'pointer',
  },
}));

function TopPostContributingUsers(props) {
  const {
    title = '',
    users = [],
    additionalField = null,
    tooltip = '',
    showViewAll = false,
    onViewAllClick = () => {},
    viewAllOnTitleClick = true,
    view = 'group',
    post = null,
    postType = '',
  } = props;

  const showPostContributorDialog = useStore(
    (state) => state.postContributorDialog.showDialog
  );

  const history = useRouter();

  const onProfileClick = (user) => {
    if (!user.isAnonymous && !user.isPublicDonation) {
      history.push(`/profile/${user.id}`);
    }
  };

  const handleShowAllContributors = () => {
    if (viewAllOnTitleClick && post) {
      showPostContributorDialog(post, postType, 'contributors');
    }
  };

  if (isEmpty(users)) {
    return null;
  }

  return (
    <Stack spacing={1} mt={1}>
      {!isEmpty(title) && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center">
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {viewAllOnTitleClick ? (
              <StyledAnchor
                fontWeight="bold"
                variant="body2"
                role="button"
                onClick={handleShowAllContributors}>
                {title}
              </StyledAnchor>
            ) : (
              <StyledBoldTypography variant="body2">
                {title}
              </StyledBoldTypography>
            )}
            {!isEmpty(tooltip) && <ClickableTooltip message={tooltip} />}
          </Stack>

          {showViewAll && (
            <StyledLinkButton
              type="button"
              onClick={onViewAllClick}
              fontSize="0.75rem"
              fontWeight={500}>
              View All
            </StyledLinkButton>
          )}
        </Stack>
      )}
      {view === 'details' && (
        <Stack direction="row" spacing={2}>
          {users.map((user, index) => (
            <Stack
              key={`${user.first_name}_${user.id}_${index}`}
              role="button"
              onClick={() => onProfileClick(user)}
              alignItems="center">
              <Avatar
                src={
                  user.isAnonymous
                    ? '/assets/images/blank-profile.png'
                    : getQualifiedImageUrl(user.avatar)
                }
                sx={{
                  cursor:
                    user.isAnonymous || user.isPublicDonation
                      ? 'default'
                      : 'pointer',
                }}>
                {getAvatarText(getFullName(user))}
              </Avatar>
              <StyledBoldTypography variant="caption">
                {`${user.first_name} ${take(user.last_name, 1)}`}
              </StyledBoldTypography>
              {!isEmpty(additionalField) && !isEmpty(user[additionalField]) && (
                <StyledBoldTypography variant="caption">
                  {user[additionalField]}
                </StyledBoldTypography>
              )}
            </Stack>
          ))}
        </Stack>
      )}
      {view === 'group' && (
        <StyledAvatarGroup
          max={Math.max(users.length, 2)}
          sx={{ alignSelf: 'flex-start' }}>
          {users.map((user, index) => (
            <Tooltip
              key={`${user.first_name}_${user.id}_${index}`}
              title={`${user.first_name} ${user.last_name}`}>
              <Avatar
                src={
                  user.isAnonymous
                    ? '/assets/images/blank-profile.png'
                    : getQualifiedImageUrl(user.avatar)
                }
                sx={{
                  backgroundColor: user.isAnonymous && 'background.innerGray',
                  cursor:
                    user.isAnonymous || user.isPublicDonation
                      ? 'default !important'
                      : 'pointer',
                }}
                alt={getFullName(user)}
                onClick={() => onProfileClick(user)}>
                {getAvatarText(getFullName(user))}
              </Avatar>
            </Tooltip>
          ))}
        </StyledAvatarGroup>
      )}
    </Stack>
  );
}

export default TopPostContributingUsers;
